import React, { useRef, useState, useMemo } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Box, Environment, ContactShadows } from '@react-three/drei';
import * as THREE from 'three';

// --- Yardımcı Fonksiyonlar ---
// Belirli bir aralığa voxel eklemek için
const addRect = (blocks, x1, x2, y1, y2, z1, z2, color, s) => {
  for (let x = x1; x <= x2; x++) {
    for (let y = y1; y <= y2; y++) {
      for (let z = z1; z <= z2; z++) {
        // Rastgele varyasyon (doğal kürk görünümü için hafif renk değişimi)
        let finalColor = color;
        if (Math.random() > 0.95 && color !== '#111') {
           // Çok hafif koyuluk ekle
        }
        blocks.push({ position: [x * s, y * s, z * s], color: finalColor });
      }
    }
  }
};

function VoxelLynx({ position, onClick, isAnimating }) {
  const groupRef = useRef();
  const [hovered, setHovered] = useState(false);

  const voxelBlocks = useMemo(() => {
    const blocks = [];
    const s = 0.05; // Voxel boyutu
    
    // --- Renk Paleti ---
    const colors = {
      fur: '#FC5D38',      // Ana kürk (Tarçın/Turuncu)
      lightFur: '#E6C6AA', // Karın altı ve ağız (Krem)
      darkFur: '#FC5D38',  // Gölgeler ve bacaklar
      black: '#1A1A1A',    // Kulak uçları ve kuyruk ucu
      nose: '#CC5555',     // Burun
      eyes: '#FFD700',     // Gözler (Altın sarısı)
      pupil: '#000000',    // Göz bebeği
      white: '#FFFFFF'     // Göz parıltısı ve bıyık kökleri
    };

    // --- 1. GÖVDE (Body) ---
    // Ana gövde (hafif eğimli duruş için koordinatlar)
    addRect(blocks, -3, 3, 6, 11, -5, 5, colors.fur, s);
    // Alt karın (Krem rengi)
    addRect(blocks, -2, 2, 6, 7, -4, 4, colors.lightFur, s);
    // Sırt detayı (Hafif çıkıntı)
    addRect(blocks, -2, 2, 11, 12, -4, 4, colors.fur, s);

    // --- 2. KAFA (Head) ---
    // Kafa tabanı
    addRect(blocks, -3, 3, 12, 17, 5, 10, colors.fur, s);
    
    // Yanak Tüyleri (Lynx'in en belirgin özelliği - Ruffs)
    // Sol yanak
    addRect(blocks, -4, -3, 12, 15, 6, 9, colors.lightFur, s);
    addRect(blocks, -5, -4, 11, 14, 7, 8, colors.lightFur, s); // Sivri uç
    // Sağ yanak
    addRect(blocks, 3, 4, 12, 15, 6, 9, colors.lightFur, s);
    addRect(blocks, 4, 5, 11, 14, 7, 8, colors.lightFur, s); // Sivri uç

    // Ağız/Burun kısmı (Muzzle)
    addRect(blocks, -1, 1, 12, 14, 10, 11, colors.lightFur, s);
    blocks.push({ position: [0, 14.5 * s, 11.1 * s], color: colors.nose }); // Burun

    // Gözler
    // Sol
    blocks.push({ position: [-1.5 * s, 15 * s, 10.1 * s], color: colors.eyes });
    blocks.push({ position: [-1.5 * s, 15 * s, 10.2 * s], color: colors.pupil });
    // Sağ
    blocks.push({ position: [1.5 * s, 15 * s, 10.1 * s], color: colors.eyes });
    blocks.push({ position: [1.5 * s, 15 * s, 10.2 * s], color: colors.pupil });

    // --- 3. KULAKLAR (Ears) ---
    // Vaşak kulakları uzun ve püsküllüdür
    
    // Sol Kulak Tabanı
    addRect(blocks, -3, -1, 17, 19, 6, 8, colors.fur, s);
    addRect(blocks, -3, -1, 19, 21, 6, 8, colors.fur, s); // Orta
    // Sol Püskül (Tuft - Siyah)
    addRect(blocks, -3, -3, 21, 23, 7, 7, colors.black, s); 

    // Sağ Kulak Tabanı
    addRect(blocks, 1, 3, 17, 19, 6, 8, colors.fur, s);
    addRect(blocks, 1, 3, 19, 21, 6, 8, colors.fur, s); // Orta
    // Sağ Püskül (Tuft - Siyah)
    addRect(blocks, 3, 3, 21, 23, 7, 7, colors.black, s);

    // --- 4. BACAKLAR (Legs) ---
    // Ön Sol
    addRect(blocks, -3, -1, 0, 6, 5, 7, colors.fur, s);
    addRect(blocks, -3, -1, 0, 1, 5, 8, colors.lightFur, s); // Pati
    
    // Ön Sağ
    addRect(blocks, 1, 3, 0, 6, 5, 7, colors.fur, s);
    addRect(blocks, 1, 3, 0, 1, 5, 8, colors.lightFur, s); // Pati

    // Arka Sol (Vaşakların arka bacakları güçlüdür, biraz kalça ekleyelim)
    addRect(blocks, -3.5, -1, 3, 7, -5, -2, colors.darkFur, s); // Kalça
    addRect(blocks, -3, -1, 0, 4, -5, -3, colors.fur, s); // Alt bacak
    addRect(blocks, -3, -1, 0, 1, -5, -2, colors.lightFur, s); // Pati

    // Arka Sağ
    addRect(blocks, 1, 3.5, 3, 7, -5, -2, colors.darkFur, s); // Kalça
    addRect(blocks, 1, 3, 0, 4, -5, -3, colors.fur, s); // Alt bacak
    addRect(blocks, 1, 3, 0, 1, -5, -2, colors.lightFur, s); // Pati

    // --- 5. KUYRUK (Tail) ---
    // Kısa ve ucu siyah (Bobtail)
    addRect(blocks, -0.5, 0.5, 9, 10, -7, -5, colors.fur, s);
    addRect(blocks, -0.5, 0.5, 9, 10, -8, -7, colors.black, s); // Siyah uç

    // --- 6. DETAYLAR (Benekler) ---
    // Rastgele değil, estetik benekler ekleyelim
    const spots = [
      [0, 12, 0], [2, 10, -2], [-2, 10, -2], 
      [0, 13, -3], [2, 9, 2], [-2, 9, 2],
      [3, 6, 0], [-3, 6, 0] // Bacak üstleri
    ];
    
    spots.forEach(([x, y, z]) => {
      blocks.push({ position: [x * s, y * s, z * s], color: colors.darkFur });
    });

    return blocks;
  }, []);

  useFrame((state) => {
    if (groupRef.current) {
      const t = state.clock.getElapsedTime();
      
      // Nefes alma efekti (sürekli)
      groupRef.current.position.y = position[1] + Math.sin(t * 2) * 0.02;

      if (isAnimating) {
        // Zıplama / Saldırı animasyonu
        const jumpHeight = Math.abs(Math.sin(t * 8)) * 0.5;
        groupRef.current.position.y = position[1] + jumpHeight;
        groupRef.current.rotation.x = Math.sin(t * 8) * 0.2; // Öne eğilme
      } else {
        // Hover efekti
        const targetScale = hovered ? 1.05 : 1;
        groupRef.current.scale.lerp(new THREE.Vector3(targetScale, targetScale, targetScale), 0.1);
        groupRef.current.rotation.x = THREE.MathUtils.lerp(groupRef.current.rotation.x, 0, 0.1);
      }
    }
  });

  return (
    <group 
      ref={groupRef} 
      position={position}
      onClick={onClick}
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
    >
      {voxelBlocks.map((block, i) => (
        <Box 
          key={i} 
          position={block.position} 
          args={[0.05, 0.05, 0.05]} // Block size matches 's'
          castShadow
          receiveShadow
        >
          <meshStandardMaterial 
            color={block.color} 
            roughness={0.8}
            metalness={0.1}
          />
        </Box>
      ))}
    </group>
  );
}

// Otomatik kamera rotasyonu için component
function AutoRotatingCamera() {
  const { camera } = useThree();
  
  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    const radius = 5;
    const speed = 0.3; // Dönüş hızı
    
    // Kamerayı modelin etrafında döndür
    camera.position.x = Math.cos(t * speed) * radius;
    camera.position.z = Math.sin(t * speed) * radius;
    camera.position.y = 2; // Yükseklik sabit kalır
    camera.lookAt(0, 0, 0); // Kamerayı merkeze bakacak şekilde ayarla
  });
  
  return null;
}

export default function VoxelLynxScene() {
  const [isAnimating, setIsAnimating] = useState(false);

  const handleLynxClick = () => {
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 1000);
  };

  return (
    <div className="w-full h-full  bg-transparent flex flex-col items-center justify-center relative">

      <Canvas shadows camera={{ position: [3, 2, 4], fov: 30 }}>
        <color attach="background" args={['#FEFBF7']} />
        
        {/* Işıklandırma */}
        <ambientLight intensity={0.6} />
        <spotLight 
          position={[10, 10, 10]} 
          angle={0.15} 
          penumbra={1} 
          intensity={1.5} 
          castShadow 
          shadow-mapSize={[1024, 1024]}
        />
        <pointLight position={[-10, -10, -10]} intensity={0.5} color="#blue" />

        {/* Sahne */}
        <group position={[0, -0.5, 0]}>
            <VoxelLynx 
                position={[0, 0, 0]} 
                onClick={handleLynxClick} 
                isAnimating={isAnimating} 
            />
            
            {/* Zemin Gölgeleri */}
            <ContactShadows 
            resolution={1024} 
            scale={10} 
            blur={2} 
            opacity={0.5} 
            far={10} 
            color="#000000" 
            />
        </group>

        <AutoRotatingCamera />
        <Environment preset="sunset" />
      </Canvas>
    </div>
  );
}